import React, { useEffect, useMemo, useState } from "react"
import ProductForm from "@components/ProductForm/ProductForm"
import {
  prepareVariantsWithOptions,
  prepareVariantsImages,
  getPrice,
} from "@lib/swell/storefront-data-hooks/src/utils/product"
import { ImageCarousel, LoadingDots } from "@components/ui"
import ProductLoader from "../ProductLoader"
import Image from "next/image"
import { Product, ProductDetail, ProductDetails } from "@lib/swell/storefront-data-hooks/src/types"
export interface ProductProps {
  className?: string
  children?: any
  product: Product
  renderSeo?: boolean
  description?: string
  title?: string
}

import { Tab, Disclosure } from "@headlessui/react"
import { MinusSmallIcon, PlusSmallIcon } from "@heroicons/react/24/outline"
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from "@heroicons/react/20/solid"
import { classNames } from "core/utils"
import Link from "next/link"

import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
  DotGroup,
  Dot,
} from "pure-react-carousel"
import "pure-react-carousel/dist/react-carousel.es.css"

function Divider() {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-gray-300" />
      </div>
      <div className="relative flex justify-center">
        <span className="bg-white px-2 text-gray-500">
          <ArrowDownIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
        </span>
      </div>
    </div>
  )
}
interface ProductBoxProps {
  className?: string
  product: string | any
  swellProduct: string | any
  relatedProducts?: string | any
  renderSeo?: boolean
  description?: string
  title?: string
}
export function ProductBox({
  product,
  swellProduct,
  relatedProducts,
  renderSeo = true,
  description = product.description,
  title = product.name,
}: ProductBoxProps) {
  const [open, setOpen] = useState(false)
  const variants = useMemo(() => prepareVariantsWithOptions(product), [product])

  const [variant, setVariant] = useState(variants[0] || null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const variantImages = variant?.images || []
  const allProductImages = product?.images?.map((image: any) => ({ ...image })) || []
  const allVariantImages =
    variant?.images?.map((image: any) => ({ ...image, src: image.file?.url })) || []
  const allImages = [...allVariantImages, ...allProductImages]
  const uniqueImages = [...new Map(allImages.map((m) => [m.src, m])).values()]
  const leftCarouselClick = () => {
    setSelectedIndex((selectedIndex - 1 + uniqueImages?.length) % uniqueImages?.length)
  }
  const rightCarouselClick = () => {
    setSelectedIndex((selectedIndex + 1 + uniqueImages?.length) % uniqueImages?.length)
  }

  useEffect(() => {
    setSelectedIndex(0)
  }, [variant])

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-2xl px-4 py-20 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8">
        <div className="relative lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-8">
          <div className="py-4 lg:sticky lg:top-20">
            {product.categories.length > 0 && (
              <Link href={`/collection/${product.categories[0].slug}`} passHref>
                <div className="mt-2 flex items-center space-x-2">
                  <ArrowLeftIcon className="text-primary h-5 w-5" aria-hidden="true" />
                  <span>{product.categories[0].name}</span>
                </div>
              </Link>
            )}

            {/* Image gallery */}
            {/* Mobile */}
            <div className="lg:hidden">
              <CarouselProvider
                naturalSlideWidth={5}
                naturalSlideHeight={4}
                totalSlides={uniqueImages.length}
                infinite={true}
              >
                <div className="relative mb-2">
                  <Slider>
                    {uniqueImages?.map((image: any, index: number) => (
                      <Slide key={index} index={index}>
                        <section className="flex h-full items-center justify-center bg-white px-6 py-8">
                          <figure className="mx-auto grid max-w-7xl grid-cols-1">
                            <div className="col-span-1 w-full">
                              <img
                                alt={image.alt || "Product image"}
                                src={image.src}
                                className="rounded-2xl object-contain"
                              />
                            </div>
                          </figure>
                        </section>
                      </Slide>
                    ))}
                  </Slider>
                </div>

                <div className="relative mx-auto flex space-x-2 text-center">
                  <ButtonBack className="absolute top-1/2 left-0 z-10 -translate-y-1/2 transform">
                    <ChevronLeftIcon className="h-8 w-8 text-black" aria-hidden="true" />
                  </ButtonBack>
                  <div className="mx-8 flex-1">
                    <DotGroup />
                  </div>
                  <ButtonNext className="absolute top-1/2 right-0 z-10 -translate-y-1/2 transform">
                    <ChevronRightIcon className="h-8 w-8 text-black" aria-hidden="true" />
                  </ButtonNext>
                </div>
              </CarouselProvider>
            </div>

            {/* Desktop */}
            <div className="hidden lg:block">
              <Tab.Group
                as="div"
                className="mt-2 flex flex-col-reverse lg:mt-4"
                selectedIndex={selectedIndex}
                // @ts-ignore
                onChange={setSelectedIndex}
              >
                {/* Image selector */}
                <div className="mt-6 w-full max-w-2xl sm:block lg:max-w-none">
                  <Tab.List className="grid grid-cols-4 gap-6">
                    {uniqueImages?.map((image: any, index: number) => (
                      <Tab
                        key={index}
                        className="focus:ring-opacity-50 relative flex h-24 cursor-pointer items-center justify-center rounded-md bg-white text-sm font-medium text-gray-900 uppercase hover:bg-gray-50 focus:ring-3 focus:ring-offset-4 focus:outline-hidden"
                      >
                        {({ selected }) => (
                          <>
                            <span className="sr-only">{image.caption}</span>
                            <span className="absolute inset-0 overflow-hidden rounded-md">
                              <Image
                                fill={true}
                                src={image.src ?? "https://via.placeholder.com/1050x1050"}
                                alt=""
                                className="h-full w-full object-contain object-center"
                              />
                            </span>
                            <span
                              className={classNames(
                                selected ? "ring-primary-500" : "ring-transparent",
                                "pointer-events-none absolute inset-0 rounded-md ring-2 ring-offset-2",
                              )}
                              aria-hidden="true"
                            />
                          </>
                        )}
                      </Tab>
                    ))}
                  </Tab.List>
                </div>

                <div className="relative">
                  <button
                    onClick={leftCarouselClick}
                    className="absolute top-1/2 left-0 z-10 -translate-y-1/2 transform"
                  >
                    <ChevronLeftIcon className="h-12 w-12 text-gray-500" aria-hidden="true" />
                  </button>

                  <Tab.Panels className="aspect-h-2 aspect-w-3 relative w-full">
                    {uniqueImages?.map((image: any, index: number) => (
                      <Tab.Panel key={index}>
                        <Image
                          fill={true}
                          src={image.src ?? "https://via.placeholder.com/1050x1050"}
                          alt={image.alt}
                          className="h-full w-full object-contain object-center sm:rounded-lg"
                        />
                      </Tab.Panel>
                    ))}
                  </Tab.Panels>
                  <button
                    onClick={rightCarouselClick}
                    className="absolute top-1/2 right-0 z-10 -translate-y-1/2 transform"
                  >
                    <ChevronRightIcon className="h-12 w-12 text-gray-500" aria-hidden="true" />
                  </button>
                </div>
              </Tab.Group>
            </div>
          </div>

          {/* Product info */}
          <div className="mt-2 px-4 sm:px-0">
            <div className="sticky z-10 lg:top-20">
              <div className="py-4">
                <div className="flex items-center justify-between space-x-9 rounded-lg bg-slate-50/90 px-4 py-3 text-sm text-slate-900 ring-1 ring-slate-900/10 backdrop-blur-xs">
                  <h1 className="text-sm tracking-tight text-gray-900 lg:text-2xl lg:font-extrabold">
                    {product.name}
                  </h1>

                  <div className="space-y-2">
                    <h2 className="sr-only">Product information</h2>
                    {product.sale && (
                      <>
                        <p className="text-base font-medium text-gray-900 lg:text-2xl">
                          {getPrice(
                            variant ? variant?.orig_price : product.orig_price,
                            product.currency ?? "USD",
                          )}
                        </p>

                        <span className="inline-flex items-center rounded-full bg-red-100 px-1 py-0.5 text-xs font-medium text-red-800">
                          - {getPrice(product.orig_price - product.price, "USD")}
                        </span>
                        <Divider />
                      </>
                    )}

                    <p className="text-base font-medium text-gray-900 lg:text-2xl">
                      {getPrice(
                        variant ? variant?.price : product.price,
                        product.currency ?? "USD",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="sr-only">Description</h3>

              <div
                className="prose space-y-6 text-base text-gray-700"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </div>
            <ProductForm
              product={product}
              swellProduct={swellProduct}
              variant={variant}
              setVariant={setVariant}
            />
            <section aria-labelledby="details-heading" className="mt-12">
              <h2 id="details-heading" className="sr-only">
                Additional details
              </h2>

              <div className="divide-y divide-gray-200 border-t">
                {product.content?.product_details?.map((details: ProductDetails) => (
                  <Disclosure as="div" key={details.name}>
                    {({ open }) => (
                      <>
                        <h3>
                          <Disclosure.Button className="group relative flex w-full items-center justify-between py-6 text-left">
                            <span
                              className={classNames(
                                open ? "text-primary-600" : "text-gray-900",
                                "text-sm font-medium",
                              )}
                            >
                              {details.name}
                            </span>
                            <span className="ml-6 flex items-center">
                              {open ? (
                                <MinusSmallIcon
                                  className="text-primary-400 group-hover:text-primary-500 block h-6 w-6"
                                  aria-hidden="true"
                                />
                              ) : (
                                <PlusSmallIcon
                                  className="block h-6 w-6 text-gray-400 group-hover:text-gray-500"
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          </Disclosure.Button>
                        </h3>
                        <Disclosure.Panel as="div" className="prose prose-sm pb-6">
                          <ul role="list">
                            {details.detail.map((detail: ProductDetail) => (
                              <li key={detail.id}>{detail.detail}</li>
                            ))}
                          </ul>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

const ProductView: React.FC<{
  product: string | any
  builderState: any
  renderSeo?: boolean
  description?: string
  title?: string
}> = ({ product, builderState, ...props }) => {
  return (
    <ProductLoader product={product}>
      {(productObject) => (
        <ProductBox
          {...props}
          product={productObject}
          relatedProducts={builderState.state.relatedProducts}
          swellProduct={builderState.state.swellProduct}
        />
      )}
    </ProductLoader>
  )
}
export default ProductView
